import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin } from "lucide-react";
import { Marquee } from "@/components/Marquee";
import { RecipeSpotlight } from "@/components/RecipeSpotlight";
import { Reveal, MaskedLine, Stagger, StaggerItem } from "@/components/Reveal";
import { LeadCapture } from "@/components/LeadCapture";
import { CONDITIONS } from "@/data/conditions";
import { getNextDrop } from "@/data/drops";
import { RECIPES } from "@/data/recipes";
import { IMAGES } from "@/data/site";
import { SEO } from "@/components/Seo";

const ACCENTS = {
  turmeric: "bg-turmeric-light text-turmeric-deep",
  beet: "bg-beet-light text-beet",
  leaf: "bg-leaf-light text-leaf",
  clay: "bg-clay-light text-clay-deep",
};

const MANIFESTO = [
  {
    n: "01",
    t: "Rooted in Bio-availability",
    d: "A nutrient on a label is not a nutrient in your blood. We obsess over pairing, fermentation and preparation — the quiet chemistry that decides what your body actually keeps.",
    testId: "manifesto-chapter-1",
  },
  {
    n: "02",
    t: "No Shaming, Just Science",
    d: "No detoxes, no guilt, no before-and-after photos. We explain mechanisms in plain language and let you make the call. Food is culture and comfort first; optimisation comes second.",
    testId: "manifesto-chapter-2",
  },
  {
    n: "03",
    t: "Hyper-Local Spice Synergy",
    d: "Haldi with black pepper. Dal with rice. Methi with your morning. The Indian pantry already holds the combinations modern nutrition keeps re-discovering. We just explain why.",
    testId: "manifesto-chapter-3",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-20, 60]);
  const tagY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <div data-testid="home-page">
      <SEO
        title="Pranivaa Kitchens — Clean Veg & Vegan Food, Backed by Science"
        description="Clean vegetarian & vegan food explained with real science. Guides for diabetes, PCOS, thyroid, gut, heart & weight. Launching in Delhi NCR & Bengaluru."
        path="/"
      />
      {/* HERO */}
      <section ref={heroRef} className="relative pt-28 md:pt-40 pb-16 md:pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.8 }}
              className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-6"
              data-testid="hero-overline"
            >
              Clean Veg & Vegan — Delhi NCR × Bengaluru
            </motion.p>
            <h1
              data-testid="hero-headline"
              className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.04] text-ink font-semibold"
            >
              <MaskedLine delay={0.15}>Real food.</MaskedLine>
              <MaskedLine delay={0.3}>Real science.</MaskedLine>
              <MaskedLine delay={0.45} className="italic text-transparent bg-clip-text bg-gradient-to-r from-beet via-clay-deep to-turmeric-deep">
                Real prana.
              </MaskedLine>
            </h1>
            <motion.p
              data-testid="hero-subheadline"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="mt-7 text-base md:text-lg leading-relaxed text-ink-soft max-w-xl"
            >
              Pranivaa Kitchens explains the science behind clean plant-forward eating — how everyday
              Indian ingredients support the management of lifestyle conditions. No fads. No fear.
              Just your kitchen, decoded.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link
                to="/coming-soon"
                data-testid="hero-cta-waitlist"
                className="group inline-flex items-center gap-2 rounded-full bg-beet text-white font-semibold text-sm px-7 py-4 transition-all duration-300 hover:bg-beet-deep hover:-translate-y-0.5 hover:shadow-xl"
              >
                Join the Waitlist
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/science"
                data-testid="hero-cta-science"
                className="inline-flex items-center gap-2 rounded-full border-2 border-ink/15 text-ink font-semibold text-sm px-7 py-4 transition-all duration-300 hover:border-ink hover:-translate-y-0.5"
              >
                Explore the Science
              </Link>
            </motion.div>
          </div>
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-turmeric-light arch-frame -rotate-2" aria-hidden="true" />
              <div className="relative arch-frame overflow-hidden shadow-2xl">
                <motion.img
                  style={{ y: imgY }}
                  src={IMAGES.heroThali}
                  alt="Authentic Indian vegetarian thali with vibrant turmeric curries, legumes and greens"
                  className="w-full h-[420px] md:h-[520px] object-cover scale-110"
                />
              </div>
              <motion.div style={{ y: tagY }} className="absolute -left-4 top-16 md:-left-8">
                <span className="inline-flex items-center gap-1.5 bg-paper-card shadow-lg rounded-full px-4 py-2 text-xs font-semibold text-ink border border-[#E7E2D7]">
                  <MapPin size={12} className="text-clay" /> Delhi NCR
                </span>
              </motion.div>
              <motion.div style={{ y: tagY }} className="absolute -right-3 bottom-20 md:-right-6">
                <span className="inline-flex items-center gap-1.5 bg-beet text-white shadow-lg rounded-full px-4 py-2 text-xs font-semibold">
                  <MapPin size={12} className="text-turmeric" /> Bengaluru
                </span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* NAVRATRI DROP BANNER */}
      <section className="bg-gradient-to-r from-beet via-clay-deep to-turmeric-deep">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white text-sm font-medium text-center sm:text-left">
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-turmeric-light mr-3">Limited Edition</span>
            {(() => { const d = getNextDrop(); return d ? `${d.name} — festive bowls, landing ${d.dateLabel}.` : "New seasonal drops are announced to the waitlist first."; })()}
          </p>
          <Link
            to="/coming-soon"
            data-testid="home-drop-banner-link"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white/15 border border-white/25 text-white text-xs font-semibold px-4 py-2 transition-all hover:bg-white/25"
          >
            See the drop menu <ArrowRight size={13} />
          </Link>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-6">
              The Problem
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-snug tracking-tight text-ink font-medium">
              Lifestyle conditions are rising across urban India — and most food advice is either{" "}
              <em className="text-beet">fear</em> or <em className="text-leaf">fads</em>. We think you
              deserve an <em className="text-clay-deep">explanation</em> instead.
            </h2>
          </Reveal>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="py-16 md:py-24 bg-paper-muted/60 border-y border-[#E7E2D7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-3">
              Our Philosophy
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink font-semibold mb-14">
              Three chapters we cook by.
            </h2>
          </Reveal>
          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            {MANIFESTO.map((m) => (
              <StaggerItem key={m.n} testId={m.testId} className="group">
                <p className="font-serif text-6xl md:text-7xl font-semibold text-turmeric/80 leading-none transition-colors duration-500 group-hover:text-clay">
                  {m.n}
                </p>
                <h3 className="font-serif text-xl md:text-2xl font-semibold text-ink mt-5 mb-3">{m.t}</h3>
                <p className="text-sm leading-relaxed text-ink-soft">{m.d}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* SCIENCE GRID */}
      <section className="py-16 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-3">
                The Science Hub
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink font-semibold max-w-xl">
                Six conditions. Zero jargon. One honest kitchen.
              </h2>
            </div>
            <Link
              to="/science"
              data-testid="home-science-hub-link"
              className="editorial-link inline-flex items-center gap-2 text-sm font-semibold text-beet shrink-0"
            >
              Open the full hub <ArrowUpRight size={16} />
            </Link>
          </Reveal>
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {CONDITIONS.map((c) => (
              <StaggerItem key={c.slug}>
                <Link
                  to={`/science/${c.slug}`}
                  data-testid={`science-card-${c.slug}`}
                  className="group block bg-paper-card border border-[#E7E2D7] rounded-2xl p-7 h-full transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:border-clay/40"
                >
                  <span className={`inline-block font-mono text-[11px] tracking-[0.15em] uppercase font-semibold rounded-full px-3 py-1.5 ${ACCENTS[c.accent]}`}>
                    {c.navName}
                  </span>
                  <h3 className="font-serif text-2xl font-semibold text-ink mt-5 mb-2 leading-snug">
                    {c.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-ink-soft mb-6">{c.tagline}</p>
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wide text-clay-deep uppercase">
                    Read the science
                    <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* PHILOSOPHY TEASER */}
      <section className="py-16 md:py-24 bg-leaf-light/50 border-y border-[#E7E2D7] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5 relative order-2 lg:order-1">
            <div className="absolute -inset-3 bg-turmeric/20 rounded-3xl rotate-2" aria-hidden="true" />
            <img loading="lazy" decoding="async"
              src={IMAGES.thaliPlatter}
              alt="Traditional stainless steel thali with clean regional Indian dishes"
              className="relative rounded-3xl shadow-xl w-full h-[380px] object-cover transition-transform duration-700 hover:scale-[1.02]"
            />
          </Reveal>
          <div className="lg:col-span-7 order-1 lg:order-2 lg:pl-8">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-4">
                Who We Are
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink font-semibold mb-6 leading-snug">
                Nourishment that keeps your life-force flowing.
              </h2>
              <p className="text-base leading-relaxed text-ink-soft mb-4 max-w-xl">
                Pranivaa comes from Sanskrit roots — food in service of <em>prana</em>, the life-force.
                We are building India's most honest clean vegetarian and vegan food brand: one that
                explains why before it asks you to buy.
              </p>
              <p className="text-base leading-relaxed text-ink-soft mb-8 max-w-xl">
                For urban professionals managing lifestyle conditions, we are the destination that
                explains the science behind every ingredient choice.
              </p>
              <Link
                to="/about"
                data-testid="home-philosophy-link"
                className="editorial-link inline-flex items-center gap-2 text-sm font-semibold text-beet"
              >
                Read our full philosophy <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* RECIPES TEASER */}
      <section className="py-16 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-3">
                Recipes & Rituals
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink font-semibold">
                From the journal.
              </h2>
            </div>
            <Link
              to="/recipes"
              data-testid="home-recipes-link"
              className="editorial-link inline-flex items-center gap-2 text-sm font-semibold text-beet shrink-0"
            >
              All recipes <ArrowUpRight size={16} />
            </Link>
          </Reveal>
          <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {RECIPES.map((r) => (
              <StaggerItem key={r.id}>
                <Link
                  to="/recipes"
                  data-testid={r.testId}
                  className="group block bg-paper-card border border-[#E7E2D7] rounded-2xl overflow-hidden h-full transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
                >
                  <div className="overflow-hidden h-52">
                    <img loading="lazy" decoding="async"
                      src={r.image}
                      alt={r.imageAlt}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-mono text-[11px] tracking-[0.12em] uppercase font-semibold text-clay">
                        {r.tag}
                      </span>
                      <span className="font-mono text-[11px] text-ink-muted">{r.time}</span>
                    </div>
                    <h3 className="font-serif text-xl font-semibold text-ink leading-snug mb-2">{r.title}</h3>
                    <p className="text-sm text-ink-soft leading-relaxed">{r.excerpt}</p>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* RECIPE OF THE WEEK */}
      <RecipeSpotlight />

      {/* WAITLIST CTA */}
      <section id="waitlist" className="py-16 md:py-28 bg-beet relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #C6A052 0%, transparent 40%), radial-gradient(circle at 80% 70%, #C85A32 0%, transparent 40%)" }}
          aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-turmeric font-semibold mb-4">
                Coming Soon
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white font-semibold leading-tight mb-6">
                Fresh salads & clean thalis. Delivered in Delhi NCR & Bengaluru.
              </h2>
              <p className="text-base leading-relaxed text-white/70 max-w-lg mb-8">
                Phase one is knowledge. Phase two is lunch. Join the city-tagged waitlist and be first
                to know when your neighbourhood goes live — plus get the science letters every week.
              </p>
              <div className="flex gap-6 font-mono text-xs text-white/50">
                <span>Weekly science letter</span>
                <span>·</span>
                <span>WhatsApp community</span>
                <span>·</span>
                <span>Early access</span>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="bg-white/[0.07] backdrop-blur-sm border border-white/15 rounded-3xl p-7 md:p-8">
              <LeadCapture dark source="home-hero" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
